import React, { useState } from 'react';
import type { Organization, CrisisManagementPlanResult } from '@types';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { ICONS } from './constants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { HelpSidebar } from './HelpSidebar';

interface CrisisManagementBuilderProps {
    isOpen: boolean;
    onClose: () => void;
    organization?: Organization;
    onComplete?: (result: CrisisManagementPlanResult) => void;
    inline?: boolean;
    onRequestHelp?: (preferChat?: boolean) => void;
    onNavigate?: (nav: { page: string }) => void;
    initialData?: any;
}

export const CrisisManagementBuilder: React.FC<CrisisManagementBuilderProps> = ({ 
    isOpen = true, 
    onClose = () => {}, 
    organization, 
    onComplete = () => {},
    inline = false,
    onRequestHelp,
    onNavigate,
    initialData
}) => {
    const { assets } = useGlobalAssets();
    const [step, setStep] = useState(1);
    
    // Form State
    const [incidentType, setIncidentType] = useState(initialData?.incidentType || 'Cyber Attack / Data Breach');
    const [severityLevel, setSeverityLevel] = useState(initialData?.severityLevel || 'Critical (Tier 1) - Enterprise Threat');
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [impactedFunctions, setImpactedFunctions] = useState<string[]>([]);
    
    // Engine State
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('');

    const INCIDENT_TYPES = [
        'Cyber Attack / Data Breach',
        'Kinetic / Physical Attack',
        'Natural Disaster',
        'Supply Chain Failure',
        'PR / Reputational Crisis',
        'Pandemic / Health Crisis'
    ];

    const SEVERITY_LEVELS = [
        'Critical (Tier 1) - Enterprise Threat',
        'High (Tier 2) - Regional Impact',
        'Medium (Tier 3) - Localized Impact',
        'Low (Tier 4) - Monitor Only'
    ];

    const PRESETS = [
        'IT Infrastructure / Data Centers',
        'Supply Chain / Logistics',
        'Human Resources / Payroll',
        'Financial Operations',
        'Customer Support / Call Centers',
        'Manufacturing / Production Lines'
    ];

    const toggleAsset = (id: string) => {
        setSelectedAssetIds(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
    };

    const toggleImpactedFunction = (func: string) => {
        setImpactedFunctions(prev => prev.includes(func) ? prev.filter(f => f !== func) : [...prev, func]);
    };

    const handleGenerate = async () => {
        setIsGenerating(true);
        setStep(3); // Loading step
        
        try {
            setLoadingText('Initializing Crisis Management AI Core...');
            setProgress(15);
            await new Promise(r => setTimeout(r, 1200));

            setLoadingText('Synthesizing Assets and Incident Type...');
            setProgress(45);
            
            const selectedAssetsData = assets.filter(a => selectedAssetIds.includes(a.id));

            let result: CrisisManagementPlanResult;

            const functions = getFunctions();
            const generateCrisisPlan = httpsCallable(functions, 'generateCrisisManagementPlan');
            const response = await generateCrisisPlan({
                organizationId: organization?.id || 'default',
                industry: 'Corporate',
                incidentType,
                severityLevel,
                assets: selectedAssetsData,
                impactedFunctions
            });
            result = response.data as CrisisManagementPlanResult;
            
            setProgress(85);
            setLoadingText('Formatting Crisis Response Matrix...');
            await new Promise(r => setTimeout(r, 1000));
            
            setProgress(100);
            setTimeout(() => {
                onComplete(result);
            }, 800);

        } catch (error) {
            console.error("Crisis Management Generation Error", error);
            setLoadingText('ERROR: AI Generation Failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) return null;

    if (inline) {
        return (
            <div className="w-full h-full flex flex-col relative bg-slate-900 overflow-y-auto custom-scrollbar animate-fade-in p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-8 border-b border-slate-700/50 pb-6 shrink-0">
                    <div>
                        <div className="flex items-center space-x-3 mb-2">
                            <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                {ICONS.shield}
                            </div>
                            <h2 className="text-2xl font-bold font-heading text-white tracking-wide">Crisis Management <span className="text-blue-500">Builder</span></h2>
                        </div>
                        <p className="text-slate-400">Configure parameters to generate a custom AI-driven crisis response protocol.</p>
                    </div>
                </div>

                {/* Body Content */}
                {step === 1 && (
                    <div className="space-y-8 pb-20">
                        {/* 1. Incident Type */}
                        <div>
                            <label className="block text-slate-300 font-bold mb-3 text-lg flex items-center">
                                <span className="bg-blue-600/20 text-blue-400 w-8 h-8 rounded-full flex items-center justify-center mr-3 border border-blue-500/30">1</span>
                                Select Incident Type
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {INCIDENT_TYPES.map(type => (
                                    <button 
                                        key={type}
                                        onClick={() => setIncidentType(type)}
                                        className={`p-4 rounded-xl border text-left transition-all ${
                                            incidentType === type 
                                            ? 'bg-blue-600/20 border-blue-500/50 text-blue-300 shadow-[0_0_15px_rgba(59,130,246,0.2)]' 
                                            : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-300'
                                        }`}
                                    >
                                        <div className="font-semibold">{type}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* 2. Severity Level */}
                        <div>
                            <label className="block text-slate-300 font-bold mb-3 text-lg flex items-center">
                                <span className="bg-blue-600/20 text-blue-400 w-8 h-8 rounded-full flex items-center justify-center mr-3 border border-blue-500/30">2</span>
                                Threat Severity Level
                            </label>
                            <div className="space-y-2">
                                {SEVERITY_LEVELS.map(level => {
                                    const isCritical = level.includes('Critical');
                                    const isHigh = level.includes('High');
                                    const isMedium = level.includes('Medium');
                                    
                                    let colorClasses = 'border-slate-700 bg-slate-800/50 text-slate-400 hover:bg-slate-800';
                                    if (severityLevel === level) {
                                        if (isCritical) colorClasses = 'bg-red-900/40 border-red-500/50 text-red-300 shadow-[0_0_15px_rgba(239,68,68,0.2)]';
                                        else if (isHigh) colorClasses = 'bg-orange-900/40 border-orange-500/50 text-orange-300 shadow-[0_0_15px_rgba(249,115,22,0.2)]';
                                        else if (isMedium) colorClasses = 'bg-yellow-900/40 border-yellow-500/50 text-yellow-300 shadow-[0_0_15px_rgba(234,179,8,0.2)]';
                                        else colorClasses = 'bg-blue-900/40 border-blue-500/50 text-blue-300 shadow-[0_0_15px_rgba(59,130,246,0.2)]';
                                    }

                                    return (
                                        <button 
                                            key={level}
                                            onClick={() => setSeverityLevel(level)}
                                            className={`w-full p-4 rounded-xl border text-left transition-all flex items-center justify-between ${colorClasses}`}
                                        >
                                            <span className="font-semibold tracking-wide">{level}</span>
                                            {severityLevel === level && <div className="w-3 h-3 rounded-full bg-current animate-pulse"></div>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 2.5 Impacted Functions */}
                        <div>
                            <label className="block text-slate-300 font-bold mb-3 text-lg flex items-center">
                                <span className="bg-blue-600/20 text-blue-400 w-8 h-8 rounded-full flex items-center justify-center mr-3 border border-blue-500/30">+</span>
                                Impacted Critical Functions
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                                {PRESETS.map(preset => (
                                    <button
                                        key={preset}
                                        onClick={() => toggleImpactedFunction(preset)}
                                        className={`p-3 rounded-xl border text-left text-sm transition-all flex items-center justify-between ${
                                            impactedFunctions.includes(preset)
                                            ? 'bg-blue-600/20 border-blue-500/50 text-blue-300 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
                                            : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                                        }`}
                                    >
                                        <span className="font-semibold">{preset}</span>
                                        {impactedFunctions.includes(preset) && (
                                            <div className="w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">✓</div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* 3. Affected Assets */}
                        <div>
                            <label className="block text-slate-300 font-bold mb-3 text-lg flex items-center">
                                <span className="bg-blue-600/20 text-blue-400 w-8 h-8 rounded-full flex items-center justify-center mr-3 border border-blue-500/30">3</span>
                                Primary Affected Assets
                            </label>
                            {assets.length === 0 ? (
                                <div className="text-slate-500 italic p-4 border border-slate-800 rounded-xl bg-slate-900/50">
                                    No assets registered. Plans will be generated generally.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                                    {assets.map(asset => (
                                        <div 
                                            key={asset.id}
                                            onClick={() => toggleAsset(asset.id)}
                                            className={`p-4 rounded-xl border cursor-pointer transition-all flex justify-between items-center ${
                                                selectedAssetIds.includes(asset.id)
                                                ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.1)]'
                                                : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                                            }`}
                                        >
                                            <div>
                                                <div className="font-bold text-slate-200">{asset.name}</div>
                                                <div className="text-xs opacity-75">{asset.type} • {asset.location?.address}</div>
                                            </div>
                                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${selectedAssetIds.includes(asset.id) ? 'bg-emerald-500 border-emerald-400 text-white' : 'border-slate-600'}`}>
                                                {selectedAssetIds.includes(asset.id) && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Action Footer */}
                        <div className="pt-6 border-t border-slate-700/50 flex justify-end">
                            <button
                                onClick={handleGenerate}
                                disabled={isGenerating || (assets.length > 0 && selectedAssetIds.length === 0)}
                                className={`px-8 py-4 rounded-xl font-bold text-lg tracking-wide shadow-lg transition-all flex items-center ${
                                    isGenerating || (assets.length > 0 && selectedAssetIds.length === 0)
                                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-500 hover:shadow-blue-500/25 active:scale-95'
                                }`}
                            >
                                {isGenerating ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Initializing AI Core...
                                    </>
                                ) : (
                                    <>
                                        <span className="mr-2">Generate Crisis Response Plan</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {step === 3 && (
                    <div className="flex-1 flex flex-col items-center justify-center p-12">
                        <div className="relative w-32 h-32 mb-8">
                            <div className="absolute inset-0 border-t-4 border-blue-500 rounded-full animate-spin"></div>
                            <div className="absolute inset-2 border-r-4 border-teal-500 rounded-full animate-spin direction-reverse"></div>
                            <div className="absolute inset-0 flex items-center justify-center text-blue-400">
                                {ICONS.shield}
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-4 animate-pulse">{loadingText}</h3>
                        <div className="w-full max-w-md bg-slate-800 rounded-full h-2 mb-2 overflow-hidden border border-slate-700">
                            <div 
                                className="bg-gradient-to-r from-blue-500 to-teal-400 h-2 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <div className="text-slate-500 text-sm font-mono tracking-widest">{progress}% COMPLETE</div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-4">
            <div className={`absolute inset-0 bg-slate-900/90 backdrop-blur-xl ${!isGenerating ? 'cursor-pointer' : ''}`} onClick={!isGenerating ? onClose : undefined} />
            
            <div className="relative w-full max-w-6xl glass-panel flex flex-col md:flex-row transform transition-transform animate-scale-in text-left shadow-[0_0_100px_rgba(59,130,246,0.3)] border border-blue-500/50 rounded-2xl max-h-[90vh] overflow-hidden">
                
                {/* Main Content Area */}
                <div className="flex-1 p-8 overflow-y-auto custom-scrollbar relative">
                    {/* Header */}
                <div className="flex justify-between items-start mb-8 border-b border-slate-700/50 pb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                {ICONS.shield}
                            </div>
                            <h2 className="text-2xl font-black text-white tracking-widest uppercase">
                                Crisis Management Plan Builder
                            </h2>
                        </div>
                        <p className="text-sm text-slate-400 font-mono tracking-widest pl-12 uppercase">
                            Define the parameters of the crisis to generate actionable response protocols.
                        </p>
                    </div>
                    {!isGenerating && (
                        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition-colors">
                            {ICONS.menuClose}
                        </button>
                    )}
                </div>

                {step === 1 && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Incident Category</label>
                                <select 
                                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                                    value={incidentType}
                                    onChange={(e) => setIncidentType(e.target.value)}
                                >
                                    {INCIDENT_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-4">
                                <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Severity Tier</label>
                                <select 
                                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-red-400 font-semibold focus:outline-none focus:border-red-500 transition-colors"
                                    value={severityLevel}
                                    onChange={(e) => setSeverityLevel(e.target.value)}
                                >
                                    {SEVERITY_LEVELS.map(level => (
                                        <option key={level} value={level}>{level}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end pt-6">
                            <button 
                                onClick={() => setStep(2)}
                                className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded shadow-lg shadow-blue-500/20 transition-all uppercase tracking-widest"
                            >
                                Continue to Assets
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="mb-8">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest mb-4">Impacted Critical Functions</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {PRESETS.map(preset => (
                                    <button
                                        key={preset}
                                        onClick={() => toggleImpactedFunction(preset)}
                                        className={`p-3 rounded-xl border text-left text-sm transition-all flex items-center justify-between ${
                                            impactedFunctions.includes(preset)
                                            ? 'bg-blue-600/20 border-blue-500/50 text-blue-300 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
                                            : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                                        }`}
                                    >
                                        <span className="font-semibold">{preset}</span>
                                        {impactedFunctions.includes(preset) && (
                                            <div className="w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">✓</div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between items-center mt-6 pt-6 border-t border-slate-800">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Impacted Global Assets <span className="text-slate-500">({selectedAssetIds.length} Selected)</span></label>
                            <button onClick={() => setSelectedAssetIds(assets.map(a => a.id))} className="text-xs text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest">Select All</button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-64 overflow-y-auto custom-scrollbar p-2">
                            {assets.length === 0 && (
                                <div className="col-span-2 text-center py-8 text-slate-500 font-mono text-sm border border-slate-800 border-dashed rounded-lg bg-slate-900/30">
                                    No assets registered globally. Proceeding will generate a general organizational plan.
                                </div>
                            )}
                            {assets.map(asset => (
                                <div 
                                    key={asset.id} 
                                    onClick={() => toggleAsset(asset.id)}
                                    className={`p-4 rounded-lg border cursor-pointer transition-all flex items-start gap-3 ${selectedAssetIds.includes(asset.id) ? 'bg-blue-600/20 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)]' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'}`}
                                >
                                    <input 
                                        type="checkbox" 
                                        checked={selectedAssetIds.includes(asset.id)} 
                                        onChange={() => {}} 
                                        className="mt-1 flex-shrink-0"
                                    />
                                    <div>
                                        <h4 className="font-bold text-slate-200 text-sm leading-tight">{asset.name}</h4>
                                        <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{asset.type} • {asset.location?.address || 'Unknown Location'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(1)}
                                className="px-6 py-3 border border-slate-600 hover:border-slate-500 text-slate-300 font-bold rounded transition-all uppercase tracking-widest"
                            >
                                Back
                            </button>
                            <button 
                                onClick={handleGenerate}
                                className="px-8 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded shadow-lg shadow-red-500/20 transition-all uppercase tracking-widest flex items-center gap-2"
                            >
                                {ICONS.shield}
                                Generate Crisis Plan
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="py-20 flex flex-col items-center justify-center animate-fade-in text-center">
                        <div className="mb-8 relative">
                            <div className="absolute inset-0 bg-red-500/20 blur-[50px] rounded-full animate-pulse" />
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-24 h-24 text-red-500 relative z-10 animate-[spin_4s_linear_infinite]">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                            </svg>
                        </div>
                        
                        <p className="text-red-400 font-mono text-sm tracking-widest mb-10 h-6">
                            {loadingText}
                        </p>

                        <div className="w-full max-w-lg bg-slate-800 rounded-full h-2 mb-4 overflow-hidden border border-slate-700">
                            <div 
                                className="bg-gradient-to-r from-red-600 to-orange-400 h-2 rounded-full transition-all duration-500 ease-out relative" 
                                style={{ width: `${progress}%` }}
                            >
                                <div className="absolute top-0 right-0 bottom-0 w-20 bg-white/30 blur-[4px]" />
                            </div>
                        </div>
                    </div>
                )}
                </div>

                {/* Sidebar */}
                {!inline && (
                    <HelpSidebar 
                        title="Crisis Support & AI Guidance"
                        description="Follow the steps to configure an Insights generated protocol tailored to your threat matrix."
                        instructions={[
                            "Select the primary Incident Category.",
                            "Determine Threat Severity Level based on impact.",
                            "Identify Impacted Critical Functions and select all Impacted Global Assets.",
                            "Click Continue or Generate when complete."
                        ]}
                        onRequestHelp={onRequestHelp}
                        onContactConsultant={() => onNavigate && onNavigate({ page: 'contact' })}
                        theme="dark"
                    />
                )}
            </div>
        </div>
    );
};
